import {
  Option,
  OrderItem,
  Invoice,
  EnumOrderStatus,
  OrderItemCustomerTrip,
  EnumTransportItemKind,
  User
} from 'src/types';
import dayjs from 'dayjs';
import { UITimeFormat, DBDateFormat } from 'src/lib/constants';

export function getDurationDay(option: Option) {
  const day =
    option.durationNights && option.durationNights > 0
      ? option.durationNights
      : option.durationDays
      ? option.durationDays - 1
      : 0;
  return day < 0 ? 0 : day;
}

export function hasRequiredCustomerInfo(orderItem: OrderItemCustomerTrip) {
  return orderItem.requiredCustomerInfos && orderItem.requiredCustomerInfos.length > 0;
}

export function isRequiredCustomerInfoDone(orderItem: OrderItemCustomerTrip) {
  return orderItem.customers.length > 0 && orderItem.customers.every((o) => o.isSubmitted);
}

export function hasRequiredTransportItem(orderItem: OrderItemCustomerTrip) {
  const option = orderItem.option;
  return (
    (option.transportItems || []).filter((i) => i.kind === EnumTransportItemKind.pickup).length > 0
  );
}

export function isRequiredTransportItemDone(orderItem: OrderItemCustomerTrip) {
  const option = orderItem.option
  const hasSubmittedCustomerInfo = orderItem.customers.filter((o) => o.isSubmitted).length > 0;
  return (
    orderItem.selectedTransportItem &&
    (!hasRequiredCustomerInfo(orderItem) || hasSubmittedCustomerInfo)
  );
}

export function getAllowedCustomerActions(orderItem: OrderItemCustomerTrip | undefined, user: User | undefined): {
  cancelBooking: boolean;
  changeDate: boolean;
  addGuest: boolean;
  createReview: boolean;
  shareTrip: boolean;
} {
  const order = orderItem?.order

  if (!orderItem || !order || orderItem.isGift || user?.id !== order.userId)
    return { cancelBooking: false, changeDate: false, addGuest: false, createReview: false, shareTrip: false };

  const checkoutPaymentSuccessed =
    orderItem.checkoutPayment &&
    ['captured', 'refunded', 'partial_refunded'].includes(orderItem.checkoutPayment.status);
  const twoMonthFromStartDate = dayjs(orderItem.startDate).add(2, 'month').format(DBDateFormat);

  if (EnumOrderStatus.Processing === orderItem.status) {
    return { cancelBooking: true, changeDate: false, addGuest: false, createReview: false, shareTrip: order.canShareTrip };
  } else if (EnumOrderStatus.Confirmed === orderItem.status) {
    let actions = { cancelBooking: false, addGuest: false, changeDate: false, createReview: false, shareTrip: order.canShareTrip }
    if (checkoutPaymentSuccessed) {
      actions = {...actions, cancelBooking: true, addGuest: Number(orderItem.numGuestCanAdd) > 0, changeDate: orderItem.canChangeDate}
    }
    return actions
  } else if (
    EnumOrderStatus.Completed === orderItem.status &&
    twoMonthFromStartDate > dayjs().format(DBDateFormat) &&
    orderItem.review &&
    !orderItem.review.rating
  ) {
    return { cancelBooking: false, changeDate: false, addGuest: false, createReview: true, shareTrip: false };
  } else if (EnumOrderStatus.Awaiting === orderItem.status) {
    return { cancelBooking: false, changeDate: true, addGuest: false, createReview: false, shareTrip: order.canShareTrip};
  } else if (EnumOrderStatus.HavingChangeRequest === orderItem.status) {
    return { cancelBooking: false, changeDate: false, addGuest: false, createReview: false, shareTrip: order.canShareTrip };
  } else {
    return { cancelBooking: false, changeDate: false, addGuest: false, createReview: false, shareTrip: false };
  }
}

export function getPaymentDescription({
  order,
  orderItem,
  invoice
}: {
  order?: {
    number?: string;
    customerName?: string;
    customerEmail?: string;
  };
  orderItem?: OrderItem;
  invoice?: Invoice;
}) {
  if (invoice && order?.number) {
    return `Topup payment for order #${order.number}`;
  } else if (orderItem && order) {
    const name = orderItem?.giftCardId
      ? `GC#${orderItem?.giftCardId}`
      : `SAO#${orderItem.supplierActivityOptionId}`;
    return order ? `${order.customerName} - ${order.customerEmail} - ${name}` : name;
  } else {
    return '';
  }
}

export const getTotalQty = (obj: {
  quantity?: number;
  childQuantity?: number;
  localQuantity?: number;
  childLocalQuantity?: number;
}) => {
  return (obj.quantity || 0) +
    (obj.childQuantity || 0) +
    (obj.localQuantity || 0) +
    (obj.childLocalQuantity || 0);
};

export function displayTimeslot(data: { startTime: string; endTime: string }) {
  return `${dayjs.utc(data.startTime).format(UITimeFormat)} - ${dayjs
    .utc(data.endTime)
    .format(UITimeFormat)}`;
}

export function bgClassByOrderStatus(status: string) {
  return {
    'tw-text-grey': status === 'rescheduling' || status === 'processing' || status === 'awaiting' || status === 'having_change_request',
    'tw-text-black': status === 'declined' || status === 'cancelled',
    'tw-text-green-light': status === 'completed',
    'tw-text-orange': status === 'confirmed',
  };
}
